import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Project } from '@/utils/pocketbase';

export interface WizardResult {
  name: string;
  projectId?: string;
  brief: {
    product: string;
    audience?: string;
    services?: string;
    goal?: string;
    contacts?: string;
  };
}

interface BuilderWizardProps {
  projects: Project[];
  isSubmitting: boolean;
  onSubmit: (result: WizardResult) => void;
  onCancel: () => void;
}

const NO_PROJECT = '__none__';

/**
 * Two-step create flow.
 *
 * Split so the first screen asks only what the user certainly knows — the site's name
 * — and the brief, which takes actual thought, gets a screen of its own. Only the
 * product description is mandatory: everything else the model can work around, and a
 * long required form is what stops people from ever pressing the button.
 */
export function BuilderWizard({ projects, isSubmitting, onSubmit, onCancel }: BuilderWizardProps) {
  const { t } = useTranslation('builder');
  const [step, setStep] = useState<0 | 1>(0);

  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState<string>(NO_PROJECT);
  const [product, setProduct] = useState('');
  const [audience, setAudience] = useState('');
  const [services, setServices] = useState('');
  const [goal, setGoal] = useState('');
  const [contacts, setContacts] = useState('');

  const canContinue = name.trim().length > 0;
  const canSubmit = canContinue && product.trim().length > 0;

  const submit = () => {
    if (!canSubmit || isSubmitting) return;
    onSubmit({
      name: name.trim(),
      projectId: projectId === NO_PROJECT ? undefined : projectId,
      brief: {
        product: product.trim(),
        audience: audience.trim() || undefined,
        services: services.trim() || undefined,
        goal: goal.trim() || undefined,
        contacts: contacts.trim() || undefined,
      },
    });
  };

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex items-center gap-[8px]">
        {([t('wizard.steps.about'), t('wizard.steps.brief')] as const).map((label, index) => (
          <div key={label} className="flex items-center gap-[8px]">
            <span
              className={
                'inline-flex h-[24px] min-w-[24px] items-center justify-center rounded-full px-[8px] font-manrope text-[12px] font-bold ' +
                (index <= step
                  ? 'bg-[var(--main-brand,#2197f1)] text-white'
                  : 'bg-[var(--grey-accent,#eeeeee)] text-[color:var(--grey-text,#565858)]')
              }
            >
              {index + 1}
            </span>
            <span className="font-manrope text-[14px] font-semibold text-[color:var(--grey-text,#565858)]">
              {label}
            </span>
            {index === 0 && <span className="mx-[4px] h-px w-[24px] bg-[var(--grey-accent,#eeeeee)]" />}
          </div>
        ))}
      </div>

      {step === 0 ? (
        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-name">{t('wizard.name.label')}</Label>
            <Input
              id="builder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('wizard.name.placeholder')}
              maxLength={120}
              autoFocus
            />
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.name.hint')}
            </p>
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-project">{t('wizard.project.label')}</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger id="builder-project">
                <SelectValue placeholder={t('wizard.project.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PROJECT}>{t('wizard.project.placeholder')}</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.project.hint')}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-product">{t('wizard.product.label')}</Label>
            <Textarea
              id="builder-product"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder={t('wizard.product.placeholder')}
              rows={3}
              maxLength={600}
              autoFocus
            />
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.product.hint')}
            </p>
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-audience">{t('wizard.audience.label')}</Label>
            <Input
              id="builder-audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder={t('wizard.audience.placeholder')}
              maxLength={300}
            />
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-services">{t('wizard.services.label')}</Label>
            <Textarea
              id="builder-services"
              value={services}
              onChange={(e) => setServices(e.target.value)}
              placeholder={t('wizard.services.placeholder')}
              rows={2}
              maxLength={600}
            />
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-goal">{t('wizard.goal.label')}</Label>
            <Input
              id="builder-goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t('wizard.goal.placeholder')}
              maxLength={200}
            />
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-contacts">{t('wizard.contacts.label')}</Label>
            <Input
              id="builder-contacts"
              value={contacts}
              onChange={(e) => setContacts(e.target.value)}
              placeholder={t('wizard.contacts.placeholder')}
              maxLength={200}
            />
          </div>
        </div>
      )}

      <div className="flex justify-between gap-[8px]">
        <Button variant="ghost" onClick={step === 0 ? onCancel : () => setStep(0)} disabled={isSubmitting}>
          {step === 0 ? t('wizard.cancel') : t('wizard.back')}
        </Button>

        {step === 0 ? (
          <Button onClick={() => setStep(1)} disabled={!canContinue}>
            {t('wizard.next')}
          </Button>
        ) : (
          <Button onClick={submit} disabled={!canSubmit || isSubmitting}>
            {t('wizard.submit')}
          </Button>
        )}
      </div>
    </div>
  );
}
